import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Modal } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';

const FeedbackItem = ({ feedback, isCompact = false }) => {
    const getNPSCategory = (score) => {
        const npsScore = parseInt(score);
        if (npsScore >= 0 && npsScore <= 6) return { text: 'DETRACTOR', class: 'danger' };
        if (npsScore === 7 || npsScore === 8) return { text: 'PASIVO', class: 'warning' };
        if (npsScore >= 9 && npsScore <= 10) return { text: 'PROMOTOR', class: 'success' };
        return { text: 'N/A', class: 'secondary' };
    };

    const renderStars = (satisfaction) => {
        const stars = [];
        const score = parseInt(satisfaction);
        
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <FontAwesomeIcon
                    key={i}
                    icon="star"
                    className={`${i <= score ? 'text-warning' : 'text-300'} me-1`}
                    transform={isCompact ? 'shrink-2' : ''}
                />
            );
        }
        return stars;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const npsCategory = getNPSCategory(feedback.NetPromotionScore);

    return (
        <Card className={`mb-3 ${isCompact ? 'shadow-none' : 'shadow-sm'}`}>
            <Card.Body className={isCompact ? 'p-0' : ''}>
                <Row className="g-3 align-items-center">
                    {/* Fecha */}
                    <Col xs={12}>
                        <div className="d-flex align-items-center mb-2 text-600">
                            <FontAwesomeIcon icon="calendar" className="text-primary me-2" />
                            {formatDate(feedback.Timestamp)}
                        </div>
                    </Col>

                    <Col xs={12} md={isCompact ? 6 : 4}>
                        <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon="envelope" className="text-primary me-2" />
                            <a href={`mailto:${feedback.EmailAddress}`} className="text-decoration-none text-truncate">
                                {feedback.EmailAddress}
                            </a>
                        </div>
                    </Col>
                    <Col xs={6} md={isCompact ? 3 : 4}>
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2">Satisfacci&oacute;n:</span>
                            {renderStars(feedback.Satisfaction)}
                        </div>
                    </Col>
                    <Col xs={6} md={isCompact ? 3 : 4}>
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2">NPS:</span>
                            <span className={`badge badge-soft-${npsCategory.class} fw-medium`}>
                                {npsCategory.text} ({feedback.NetPromotionScore})
                            </span>
                        </div>
                    </Col>
                    {!isCompact && (
                        <Col xs={12}>
                            <div className="border-top pt-3">
                                <p className="mb-2">
                                    <strong className="me-2">Método de contacto preferido:</strong>
                                    <span className="text-primary">{feedback.ContactWays}</span>
                                </p>
                                {feedback.Comments && (
                                    <div className="bg-light rounded p-3">
                                        <strong className="d-block mb-2">Comentarios:</strong>
                                        <p className="mb-0 text-wrap">{feedback.Comments}</p>
                                    </div>
                                )}
                            </div>
                        </Col>
                    )}
                </Row>
            </Card.Body>
        </Card>
    );
};

const FeedbackList = ({ feedbacks, hasMore, loadMore }) => {
    return (
        <div className="px-3">
            <InfiniteScroll
                dataLength={feedbacks.length}
                next={loadMore}
                hasMore={hasMore}
                loader={<LoadingSkeleton />}
                height={500}
                className="pe-2"
            >
                {feedbacks.map((feedback, index) => (
                    <FeedbackItem key={index} feedback={feedback} />
                ))}
            </InfiniteScroll>
        </div>
    );
};

const LoadingSkeleton = () => (
    <Card className="mb-3">
        <Card.Body>
            <Skeleton height={24} className="mb-2" />
            <Skeleton height={24} className="mb-2" />
            <Skeleton height={48} />
        </Card.Body>
    </Card>
);

const CustomerFeedbackList = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const { setToken, token } = useToken();
    const ITEMS_PER_PAGE = 10;
    const { showErrorToast } = useToast();

    const fetchFeedbacks = async () => {
        if (cargando) return;
        setCargando(true);

        try {
            const response = await fetch("/api/soportetecnico/ObtenerCustomerFeedbacks/?page=" + page, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data && Array.isArray(data)) {
                    const newFeedbacks = data;
                    setFeedbacks(prev => [...prev, ...newFeedbacks]);
                    setHasMore(newFeedbacks.length === ITEMS_PER_PAGE);
                    setPage(prev => prev + 1);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Error al cargar los feedbacks: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        fetchFeedbacks();
    }, []);

    return (
        <>
            <Card className="h-100">
                <FalconCardHeader
                    title="&Uacute;ltima Retroalimentaci&oacute;n del Cliente"
                    titleTag="h5"
                    className="py-2"
                    endEl={
                        <Button
                            variant="falcon-default"
                            size="sm"
                            onClick={() => setShowModal(true)}
                            disabled={feedbacks.length === 0}
                        >
                            <FontAwesomeIcon icon="eye" className="me-1" /> Ver m&aacute;s retroalimentaci&oacute;n
                        </Button>
                    }
                />
                <Card.Body className="bg-light">
                    {cargando && feedbacks.length === 0 ? (
                        <LoadingSkeleton />
                    ) : feedbacks.length > 0 ? (
                        <FeedbackItem feedback={feedbacks[0]} isCompact={true} />
                    ) : (
                        <p className="text-center mb-0 text-muted">No hay retroalimentaci&oacute;n disponible</p>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                centered
                dialogClassName="modal-90w"
            >
                <Modal.Header closeButton className="border-200">
                    <Modal.Title className="fs-4">
                        <FontAwesomeIcon icon="comments" className="text-primary me-2" />
                        Retroalimentaci&oacute;n de Clientes
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 bg-light">
                    <FeedbackList
                        feedbacks={feedbacks}
                        hasMore={hasMore}
                        loadMore={fetchFeedbacks}
                        loading={cargando}
                    />
                </Modal.Body>
                <Modal.Footer className="border-200">
                    <Button variant="falcon-default" onClick={() => setShowModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

CustomerFeedbackList.propTypes = {
};

FeedbackItem.propTypes = {
    feedback: PropTypes.shape({
        EmailAddress: PropTypes.string.isRequired,
        Satisfaction: PropTypes.string.isRequired,
        NetPromotionScore: PropTypes.string.isRequired,
        ContactWays: PropTypes.string,
        Comments: PropTypes.string,
        Timestamp: PropTypes.string.isRequired
    }).isRequired,
    isCompact: PropTypes.bool
};

FeedbackList.propTypes = {
    feedbacks: PropTypes.array.isRequired,
    hasMore: PropTypes.bool.isRequired,
    loadMore: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired
};

export default CustomerFeedbackList;